import { parse, unparse } from 'papaparse'

import {
  Button,
  Dialog,
  FileTextIcon,
  Flex,
  Heading,
  Image,
  Link,
  Paragraph,
  Text,
  toast,
  UploadIcon,
} from '@buffer-mono/popcorn'

import { FileInput } from '~publish/components/FileInput'
import * as UploadDropzone from '~publish/components/UploadDropzone'
import type { Service } from '~publish/gql/graphql'
import { useTags } from '~publish/hooks/useTags'

import type { ListItem } from '../PostItems'
import CsvExample from './csvExampleIllustration.svg'
import { CsvTemplate } from './CsvTemplate'
import type { CSVLine, ValidHeaders } from './validators'
import { validateAndConstructItem } from './validators'

import styles from './CsvUploader.module.css'

const REQUIRED_HEADERS: ValidHeaders[] = ['Text', 'Image URL', 'Tags'] as const
const getMissingHeaders = (fields: string[] | undefined): ValidHeaders[] => {
  if (!fields || fields.length === 0) {
    return [...REQUIRED_HEADERS]
  }
  return REQUIRED_HEADERS.filter((header) => !fields.includes(header))
}

export const isRowEmpty = (row: CSVLine): boolean => {
  return Object.values(row).every((v) => {
    return v == null || (typeof v === 'string' && v.trim() === '')
  })
}
export const CsvUploader = ({
  channelId,
  channelService,
  onUploadComplete,
}: {
  channelId: string
  channelService: Service
  onUploadComplete: (items: ListItem[]) => void
}): JSX.Element | null => {
  const { tags } = useTags()
  const parseCsv = (file: File): void => {
    parse<CSVLine>(file, {
      header: true,
      skipEmptyLines: true,
      // Ensure proper handling of quoted fields
      quoteChar: '"',
      escapeChar: '"',
      delimitersToGuess: [',', ';'],
      // Forcibly remove quotes from headers as papaparse fails to handle them correctly if there are duplicate headers
      transformHeader: (header) => header.replace(/"/g, ''),
      complete: (result) => {
        // Check for duplicates among required headers
        const headers = result.meta.fields ?? []
        const duplicateHeaders = REQUIRED_HEADERS.filter((requiredHeader) => {
          // Count how many times this requiredHeader appears (with or without Papa Parse suffix)
          return (
            headers.filter(
              (header) =>
                header === requiredHeader ||
                header.startsWith(`${requiredHeader}_`),
            ).length > 1
          )
        })
        if (duplicateHeaders.length > 0) {
          toast.error(
            `Duplicate column${
              duplicateHeaders.length > 1 ? 's' : ''
            }: ${duplicateHeaders.join(', ')}`,
          )
          return
        }

        const missingHeaders = getMissingHeaders(result.meta.fields)
        if (missingHeaders.length > 0) {
          toast.error(
            `Invalid CSV file. Missing column${
              missingHeaders.length > 1 ? 's' : ''
            }: ${missingHeaders.join(', ')}`,
          )
          return
        }

        // Exclude completely empty rows
        const nonEmptyRows = result.data.filter((row) => !isRowEmpty(row))
        const items = nonEmptyRows.map((row) =>
          validateAndConstructItem({ row, tags, channelId, channelService }),
        )
        onUploadComplete(items)
      },
      error: () => {
        toast.error(
          "Can't upload your file. Please ensure the format follows the template.",
        )
      },
    })
  }
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0]
    if (file) {
      parseCsv(file)
    }
  }

  const onUpload = (files: File[]): void => {
    const file = files[0]
    if (file) {
      parseCsv(file)
    }
  }

  return (
    <>
      <Dialog.Body className={styles.body}>
        <Dialog.Title size="large">Import Your Posts</Dialog.Title>
        <Flex
          direction="column"
          align="center"
          justify="center"
          className={styles.introContainer}
        >
          <Flex
            as="section"
            align="center"
            justify="between"
            gap="lg"
            className={styles.downloadSection}
          >
            <Flex
              direction="column"
              gap="sm"
              className={styles.downloadSectionDescription}
            >
              <Heading size="small">1. Prepare your CSV file</Heading>
              <Paragraph>
                Your CSV file should have three columns — one for text, one for
                an image URL, and one for tags. Download our example CSV file to
                get started, or check out&nbsp;
                <Link
                  href="https://support.buffer.com/article/926-how-to-upload-posts-in-bulk-to-buffer"
                  external
                >
                  our guide
                </Link>{' '}
                to learn more.
              </Paragraph>
              <Button
                as="a"
                href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                  unparse(CsvTemplate, {
                    quotes: true,
                    columns: REQUIRED_HEADERS,
                    quoteChar: '"',
                    escapeChar: '"',
                    header: true,
                    skipEmptyLines: true,
                  }),
                )}`}
                download="Buffer_Import_Template.csv"
                variant="secondary"
                size="medium"
              >
                <FileTextIcon /> Download CSV Template
              </Button>
            </Flex>
            <Flex
              align="center"
              justify="end"
              className={styles.downloadSectionIllustration}
            >
              <Image
                src={CsvExample}
                alt="CSV Example"
                className={styles.csvExample}
              />
            </Flex>
          </Flex>
          <Flex
            as="section"
            direction="column"
            justify="between"
            gap="sm"
            className={styles.uploadSection}
          >
            <Heading size="small">2. Upload your CSV file</Heading>
            <UploadDropzone.Root
              onDrop={onUpload}
              onPaste={onUpload}
              className={styles.uploadDropzone}
            >
              <UploadDropzone.Overlay className={styles.dropzoneOverlay}>
                <Text size="lg" color="subtle" weight="bold">
                  Drop a CSV file here
                </Text>
              </UploadDropzone.Overlay>
              <Flex
                direction="column"
                justify="center"
                align="center"
                gap="xs"
                fullHeight
                fullWidth
              >
                <Text size="md">Drag and drop your CSV file here or </Text>
                <FileInput
                  onChange={handleFileChange}
                  accept=".csv"
                  id="csv-upload"
                >
                  <Button variant="secondary" size="medium">
                    <UploadIcon />
                    Upload file
                  </Button>
                </FileInput>
              </Flex>
            </UploadDropzone.Root>
          </Flex>
        </Flex>
      </Dialog.Body>
      <Dialog.Separator />
    </>
  )
}
